import { SVTableHeader, SVTableRow } from '@/types/common';
import { formatDateTimeFromUTC } from '@/utils/date';
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  useMediaQuery,
  Card,
  Stack,
  IconButton,
  TablePagination,
} from '@mui/material';
import SVLabeledText from './SVLabeledText';
import { formatCurrency } from '@/utils/currency';
import { useNavigate } from 'react-router-dom';
import { ChevronRight } from '@mui/icons-material';
import SVButton from './SVButton';

type SVTableProps = {
  ariaLabel: string;
  headers: SVTableHeader[];
  rows: SVTableRow[];
  emptyTableContent?: string;
} & (
  | {
      hasPagination: true;
      totalRecordCount: number;
      page: number;
      setPage: React.Dispatch<React.SetStateAction<number>>;
    }
  | {
      hasPagination?: false;
      totalRecordCount?: never;
      page?: never;
      setPage?: never;
    }
);

function LargeScreenTable({ ariaLabel, headers, rows }: SVTableProps) {
  const navigate = useNavigate();

  return (
    <TableContainer>
      <Table stickyHeader aria-label={ariaLabel}>
        <TableHead>
          <TableRow>
            {headers.map(header => (
              <TableCell
                key={header.key}
                align={header.align}
                sx={{ fontWeight: 'bold' }}>
                {header.content}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(row => (
            <TableRow
              key={row.key}
              hover
              sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
              {row.cells.map(cell => {
                const { type, key, content } = cell;

                return (
                  <TableCell key={`${row.key}-${key}`} align={cell.align}>
                    {type === 'text' && <>{content?.trim()}</>}
                    {type === 'date' && (
                      <>
                        {content
                          ? formatDateTimeFromUTC(content, false, 'MM/DD/YYYY')
                          : 'N/A'}
                      </>
                    )}
                    {type === 'amount' && (
                      <>
                        {content ? formatCurrency({ amount: content }) : 'N/A'}
                      </>
                    )}
                    {type === 'link' && (
                      <IconButton onClick={() => navigate(content)}>
                        <ChevronRight color="primary" />
                      </IconButton>
                    )}
                    {type === 'custom' && <>{content}</>}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function MobileTable({ headers, rows }: SVTableProps) {
  const navigate = useNavigate();

  return (
    <Stack spacing={2}>
      {rows.map(row => {
        return (
          <Card key={row.key}>
            {row.cells.map((cell, index) => {
              const { key, type, content } = cell;
              const label = headers[index].content;

              if (type === 'custom') {
                return (
                  <SVLabeledText
                    key={key}
                    label={label}
                    customContent={content}
                  />
                );
              } else if (type === 'date') {
                return (
                  <SVLabeledText
                    key={key}
                    label={label}
                    text={
                      content
                        ? formatDateTimeFromUTC(content, false, 'MM/DD/YYYY')
                        : 'N/A'
                    }
                  />
                );
              } else if (type === 'amount') {
                return (
                  <SVLabeledText
                    key={key}
                    label={label}
                    text={
                      content
                        ? formatCurrency({
                            amount: content,
                          })
                        : 'N/A'
                    }
                  />
                );
              } else if (type === 'link') {
                return (
                  <SVButton
                    fullWidth
                    key={key}
                    variant="outlined"
                    onClick={() => navigate(content)}>
                    View More
                  </SVButton>
                );
              }

              return <SVLabeledText key={key} label={label} text={content} />;
            })}
          </Card>
        );
      })}
    </Stack>
  );
}

export default function SVTable({
  ariaLabel,
  headers,
  rows,
  emptyTableContent,
  hasPagination,
  totalRecordCount,
  page,
  setPage,
}: SVTableProps) {
  const isLargeScreen = useMediaQuery(theme => theme.breakpoints.up('lg'));

  if (!rows.length) {
    return (
      <Typography variant="h6" textAlign="center">
        {emptyTableContent ? emptyTableContent : 'No data available in table'}
      </Typography>
    );
  }

  function handleChangePage(
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) {
    if (setPage) {
      setPage(newPage);
    }
  }

  if (isLargeScreen) {
    return (
      <>
        <LargeScreenTable ariaLabel={ariaLabel} headers={headers} rows={rows} />
        {hasPagination && (
          <TablePagination
            component="div"
            count={totalRecordCount || 0}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={20}
            rowsPerPageOptions={[]}
          />
        )}
      </>
    );
  }

  return (
    <>
      <MobileTable ariaLabel={ariaLabel} headers={headers} rows={rows} />
      {hasPagination && (
        <TablePagination
          component="div"
          count={totalRecordCount || 0}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={20}
          rowsPerPageOptions={[]}
        />
      )}
    </>
  );
}
